import { ChangeEvent, ReactNode, useRef, useState } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography, Stack } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileArrowUp } from "@fortawesome/pro-solid-svg-icons"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import DeleteIcon from "@mui/icons-material/Delete"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import fallbackSvgUrl from "./no-image.png"

type SupportedFiles = "jpg" | "png" | "gif"
type SupportedMimeTypes = Record<SupportedFiles, string>

const supportedFileTypes: SupportedMimeTypes = {
    jpg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    // svg: "image/svg+xml",
}

const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB in bytes

const useStyles = makeStyles()((theme) => ({
    accordion: {
        border: "1px solid #c4c4c4",
        height: "100%",
        width: "100%",
    },
    accordionDetails: {
        width: "100%",
    },
    noImageBox: {
        display: "block",
        border: " 0.12rem dashed",
        borderRadius: "1rem",
        color: "#287faf",
        height: "300px",
        width: "100%",
    },
    image: {
        width: "100%",
        objectFit: "contain",
    },
    uploadIcon: {
        position: "relative",
        left: "50%",
        top: "35%",
        transform: "translate(-50%, -50%)",
    },
    uploadContents: {
        position: "relative",
        left: "50%",
        top: "40%",
        transform: "translate(-50%, -50%)",
    },
    fileInput: {
        opacity: 0,
        textAlign: "center",
        height: "100%",
        width: "100%",
        cursor: "pointer",
        position: "relative",
        left: "50%",
        top: "15%",
        transform: "translate(-50%, -50%)",
    },
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "0.875rem 1fr",
        background: theme.palette.tailwind.sky[50],
        borderRadius: "0.5rem",
        gap: "0.75rem",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[700],
        width: "100%",
    },
}))

function getAcceptedFiles(fileExts: SupportedMimeTypes) {
    return Object.values(fileExts).join(",")
}

function printSupportedFileTypes(fileExts: SupportedMimeTypes) {
    return Object.keys(fileExts).join(", ").toUpperCase()
}

function formatFileSize(fileSize: number) {
    if (fileSize < 1024) {
        return `${fileSize} bytes`
    } else if (fileSize >= 1024 && fileSize < 1048576) {
        return `${(fileSize / 1024).toFixed(1)} KB`
    }

    return `${(fileSize / 1048576).toFixed(1)} MB`
}

function createImagePreview(image: File, cb: (imageUrl: string) => void) {
    const reader = new FileReader()

    reader.onloadend = () => {
        cb && cb(reader.result?.toString() ?? "")
    }

    reader.readAsDataURL(image)
}

type ImageUploadFieldProps = Readonly<{
    previewImage?: string
    label?: string
    onImageChange: (image: File | null) => void
    bottomContent?: ReactNode
}>

export function ImageUploadField({ label, onImageChange, previewImage, bottomContent }: ImageUploadFieldProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(previewImage ?? "")
    const [isExpanded, setIsExpanded] = useState(true)
    const [error, setError] = useState<string>()
    const accordionRef = useRef<HTMLDivElement | null>(null)

    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null
        if (!file) return
        setError(undefined)
        if (file.size > MAX_FILE_SIZE) {
            const msg = l10n.getString("image-uploader-max-file-size-exceeded", {
                maxFileSize: formatFileSize(MAX_FILE_SIZE),
            })
            setError(msg)
            setPreviewImageUrl(null)
            onImageChange(null)
            return
        }
        createImagePreview(file, setPreviewImageUrl)
        onImageChange(file)
    }

    const handleImageLoad = () => {
        if (accordionRef.current) {
            accordionRef.current.style.height = "auto" // Reset height before recalculating
            accordionRef.current.style.height = `${accordionRef.current.scrollHeight}px`
        }
    }

    return (
        <Accordion
            expanded={isExpanded}
            elevation={0}
            onChange={() => setIsExpanded(!isExpanded)}
            className={classes.accordion}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {label ? (
                    <Typography>{label}</Typography>
                ) : (
                    <LocalizedStrict id="image-uploader-button-label">
                        <Typography>Upload Image</Typography>
                    </LocalizedStrict>
                )}
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
                {!!previewImageUrl ? (
                    <>
                        <Box width="100%" textAlign="center" ref={accordionRef}>
                            <img
                                src={previewImageUrl}
                                alt="Uploaded Image"
                                onError={() => setPreviewImageUrl(fallbackSvgUrl)}
                                onLoad={handleImageLoad}
                                className={classes.image}
                            />
                        </Box>
                        <Stack justifyContent="center" paddingTop="1rem" direction="row" spacing={2}>
                            <Button
                                disableElevation
                                size="small"
                                variant="outlined"
                                color="primary"
                                component="label"
                                endIcon={<FontAwesomeIcon icon={faFileArrowUp} />}
                            >
                                {l10n.getString("image-previewer-button-label")}
                                <input
                                    id="upload-image-hidden"
                                    accept={getAcceptedFiles(supportedFileTypes)}
                                    type="file"
                                    hidden
                                    onChange={handleImageChange}
                                />
                            </Button>

                            <Button
                                disableElevation
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => {
                                    setPreviewImageUrl(null)
                                    onImageChange(null)
                                }}
                                endIcon={<DeleteIcon />}
                            >
                                {l10n.getString("image-previewer-button-delete")}
                            </Button>
                        </Stack>
                    </>
                ) : (
                    <Box component="label" className={classes.noImageBox}>
                        <FontAwesomeIcon icon={faFileArrowUp} size="3x" className={classes.uploadIcon} />

                        <Box className={classes.uploadContents}>
                            <LocalizedStrict id="image-uploader-drag-and-drop">
                                <Typography variant="subtitle1" gutterBottom align="center">
                                    Drag and Drop Browse
                                </Typography>
                            </LocalizedStrict>

                            <LocalizedStrict
                                id="create-image-file-support"
                                vars={{
                                    supportedFileTypes: printSupportedFileTypes(supportedFileTypes),
                                    maxSize: formatFileSize(MAX_FILE_SIZE),
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    align="center"
                                    flexWrap={"wrap"}
                                    gutterBottom
                                >
                                    {`File types supported: ${printSupportedFileTypes(
                                        supportedFileTypes,
                                    )}. Max size: ${formatFileSize(MAX_FILE_SIZE)}`}
                                </Typography>
                            </LocalizedStrict>
                            {error !== undefined && (
                                <Typography variant="body2" color="error" align="center" flexWrap={"wrap"}>
                                    {error}
                                </Typography>
                            )}
                        </Box>
                        <input
                            id="upload-image"
                            accept={getAcceptedFiles(supportedFileTypes)}
                            type="file"
                            onChange={handleImageChange}
                            className={classes.fileInput}
                        />
                    </Box>
                )}
                {bottomContent ?? <></>}
            </AccordionDetails>
        </Accordion>
    )
}
